import { useState, useEffect } from 'react';
import { Table, Card, ScrollArea, LoadingOverlay, Box } from '@mantine/core';
import { SortableTableHeader, NonSortableColumnHeader } from '../../common/SortableTableHeader';
import { ColumnAveragesRow } from '../../common/ConfigurableTable';
import { CollapsibleGroupHeader } from '../components/CollapsibleGroupHeader';
import { Pagination } from '../../common/Pagination';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import type { DataLibraryAttendanceRow, ColumnAverages } from '../../../types';
import { renderAttendanceCell } from './AttendanceCellRenderers';
import classes from '../../common/ConfigurableTable/ConfigurableTable.module.css';
import { flattenGroups, type GroupNode } from '../../../utils/groupDataByField';
import { ResizableHeaderCell } from '../components/ResizableHeaderCell';
import { useDataLibraryColumnWidths } from '../useDataLibraryTableLayout';
import { ColumnWidthCell } from '../components/ColumnWidthCell';
import { DataLibraryEmptyState } from '../components/DataLibraryEmptyState';

interface AttendanceTableProps {
  data: DataLibraryAttendanceRow[];
  groupedData: GroupNode<DataLibraryAttendanceRow>[] | null;
  columns: ColumnConfig[];
  sortConfig: SortConfig | null;
  onSort: (key: string) => void;
  loading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  page: number;
  pageSize: number;
  totalCount: number;
  /** MDF-41: server averages over all matching rows */
  averages: ColumnAverages;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  hidePagination?: boolean;
}

function AttendanceRow({
  widths,
  row,
  columns,
}: {
  widths: Record<string, number>;
  row: DataLibraryAttendanceRow;
  columns: ColumnConfig[];
}) {
  return (
    <Table.Tr className={classes.tableRow}>
      {columns.map((col, index) => (
        <ColumnWidthCell
          key={col.id}
          className={index === 0 ? classes.stickyColumn : undefined}
          style={{ minWidth: 200 }}
          width={widths[col.id]}
        >
          {renderAttendanceCell(row, col.id)}
        </ColumnWidthCell>
      ))}
    </Table.Tr>
  );
}


export function AttendanceTable({
  data,
  groupedData,
  columns,
  sortConfig,
  onSort,
  loading,
  hasActiveFilters,
  onClearFilters,
  page,
  pageSize,
  totalCount,
  averages,
  totalPages,
  onPageChange,
  onPageSizeChange,
  hidePagination = false,
}: AttendanceTableProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const layout = useDataLibraryColumnWidths();

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const groupKeys = groupedData ? groupedData.map(g => g.key).join('\0') : '';
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [groupKeys]);

  const averagesRow = (
    <ColumnAveragesRow
      columns={columns}
      averages={averages}
      rows={data}
      totalCount={totalCount}
      firstCellClassName={classes.stickyColumn}
    />
  );

  const hasData = data.length > 0 || (groupedData && groupedData.length > 0);
  const showEmpty = groupedData ? groupedData.length === 0 : !loading && data.length === 0;

  const tableHeader = (
    <Table.Thead className={classes.tableHeader}>
      <Table.Tr>
        {columns.map((col, index) =>
          col.sortable ? (
            <ResizableHeaderCell key={col.id} column={col} sticky={index === 0} style={{ minWidth: 200 }} layout={layout}>
              <SortableTableHeader label={col.label} sortKey={col.id} currentSort={sortConfig} onSort={onSort} />
            </ResizableHeaderCell>
          ) : (
            <ResizableHeaderCell key={col.id} column={col} sticky={index === 0} style={{ minWidth: 200 }} layout={layout}>
              <NonSortableColumnHeader label={col.label} />
            </ResizableHeaderCell>
          ),
        )}
      </Table.Tr>
    </Table.Thead>
  );

  const showPagination = !hidePagination && totalCount > 0 && (!loading || !!hasData);

  const tableContent = (
    <>
      {tableHeader}
      <Table.Tbody>
        {averagesRow}
        {groupedData
          ? flattenGroups(groupedData, collapsedGroups).map(item =>
                'group' in item
                  ? <CollapsibleGroupHeader key={`group-${item.group.key}`} label={item.group.label} count={item.group.rows.length} depth={item.group.depth} colSpan={columns.length} columns={columns} rows={item.group.rows} isCollapsed={collapsedGroups.has(item.group.key)} onToggle={() => toggleGroup(item.group.key)} />
                  : <AttendanceRow key={item.row.row_id} row={item.row} columns={columns} widths={layout.columnWidths} />,
              )
          : <>
              {data.map(row => <AttendanceRow key={row.row_id} row={row} columns={columns} widths={layout.columnWidths} />)}
            </>
        }
      </Table.Tbody>
    </>
  );

  return (
    <Card withBorder p={0} radius={8} bg="white" style={{ boxShadow: 'none', position: 'relative' }} className={classes.tableCard}>
      <LoadingOverlay visible={loading && !!hasData} overlayProps={{ blur: 1 }} />
      <ScrollArea scrollbarSize={8} type="auto" offsetScrollbars={false}>
        <Table highlightOnHover className={classes.table}>
          {tableContent}
        </Table>
      </ScrollArea>
      {showEmpty && <DataLibraryEmptyState message="No attendance data yet" hasActiveFilters={hasActiveFilters} onClearFilters={onClearFilters} />}
      {showPagination && (
        <Box className={classes.paginationWrapper}>
          <Pagination page={page} pageSize={pageSize} total={totalCount} totalPages={totalPages} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} pageSizeOptions={[25, 50, 100, 250]} entityLabel="records" />
        </Box>
      )}
    </Card>
  );
}
